import {
  Button,
  Checkbox,
  EButtonColorVariant,
  EHelpTextVariant,
  ETextInputTypes,
  HelpText,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import TermsLink from '@/components/ui/DisclaimerLinks/TermsLink';
import {
  emailValidator,
  nameValidator,
  phoneValidator,
  setPasswordValidator,
  termsValidator,
} from '@/constants/validationRules';
import { useRouter } from '@/hooks/useRouter';
import { ISignupForm } from '@/services/types/auth/ISignupForm';
import { getClientLocale } from '@/utility/helpers';
import { logger } from '@/utility/logger';
import { navigateTo } from '@/utility/navigateTo';

import PrivacyLink from '../ui/DisclaimerLinks/PrivacyLink';
import { TFieldErrors } from './types/TFieldErrors';

export type IProps = {
  onSignup: (data: {
    name: string;
    email: string;
    phone: string;
    password: string;
    country?: string;
    token?: string;
    enableSms: boolean;
  }) => void;
  error: { error?: string } | string | null | undefined;
  reCaptchaToken: string;
  onDidAcceptTerms?: (termsState: boolean) => void;
};

const SignUpForm: React.FC<IProps> = ({ onSignup, error, reCaptchaToken, onDidAcceptTerms }) => {
  const intl = useIntl();
  const router = useRouter();
  const recaptchaRef: MutableRefObject<ReCAPTCHA | null> = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<ISignupForm & { terms: boolean }>({
    defaultValues: {
      enableSms: true,
    },
  });
  const didCheckTerms = watch('terms');
  const shouldUseRecaptcha = !!reCaptchaToken;

  useEffect(() => {
    onDidAcceptTerms?.(!!didCheckTerms);
  }, [didCheckTerms, onDidAcceptTerms]);

  const reset = () => {
    setIsSubmitting(false);
    recaptchaRef.current?.reset();
  };

  const fieldErrors: TFieldErrors<keyof ISignupForm | 'terms'> = {
    terms: {
      required: intl.formatMessage({
        defaultMessage: 'You must accept the Outdoorsy Terms of Service.',
        id: 'MGUg/m',
      }),
    },
    name: {
      generic: intl.formatMessage({
        defaultMessage: 'Full name is required',
        id: 'MSbK0Z',
      }),
    },
    password: {
      required: intl.formatMessage({
        defaultMessage: 'Password is required.',
        id: 'pRvgsc',
      }),
      minLength: intl.formatMessage({
        defaultMessage: 'Password must be at least 8 characters.',
        id: '1JjZZ5',
      }),
      maxLength: intl.formatMessage({
        defaultMessage: 'Password must be at most 50 characters.',
        id: 'quRrnb',
      }),
      pattern: intl.formatMessage({
        defaultMessage: 'Password must contain at least one number.',
        id: 'dOPV08',
      }),
    },
    phone: {
      required: intl.formatMessage({
        defaultMessage: 'Phone number is required.',
        id: 'Il7p07',
      }),
      pattern: intl.formatMessage({
        defaultMessage: 'Please enter a valid phone number.',
        id: 'H+RqPP',
      }),
    },
    email: {
      required: intl.formatMessage({
        defaultMessage: 'Email is required',
        id: 'cU/aSG',
      }),
      pattern: intl.formatMessage({
        defaultMessage: 'Please enter a valid email address.',
        id: 'xs/MJu',
      }),
    },
    enableSms: {},
  };

  const messageInputEmail = intl.formatMessage({ defaultMessage: 'Email address', id: 'hJZwTS' });
  const messageInputName = intl.formatMessage({ defaultMessage: 'Full name', id: 'yk4PT9' });
  const messageInputPassword = intl.formatMessage({ defaultMessage: 'Password', id: '5sg7KC' });
  const messageInputPhone = intl.formatMessage({ defaultMessage: 'Phone number', id: 'jdJhOL' });
  const messageInputSubmit = intl.formatMessage({
    defaultMessage: 'Create your Outdoorsy account',
    id: '4/bGKE',
  });
  const showPasswordLabel = intl.formatMessage({
    defaultMessage: 'Show password',
    id: 'JHTwdB',
  });

  const messageConfirm = intl.formatMessage(
    {
      defaultMessage:
        'I certify that I’m at least 18 years old at the time of sign up and agree to the <terms>Outdoorsy Terms of Service</terms>.',
      id: 'jl7p/x',
    },
    { terms: (label: React.ReactNode) => <TermsLink>{label}</TermsLink> },
  );

  const messageConfirmPhoneNotifications = intl.formatMessage(
    {
      defaultMessage:
        'I agree to receive SMS messages from Outdoorsy to the phone number provided. Msg frequency varies. Msg&data rates may apply. Reply STOP to opt out, HELP for help. <privacy>View Privacy Policy here</privacy>.',
      id: 'CoudIv',
    },
    {
      privacy: (label: React.ReactNode) => <PrivacyLink>{label}</PrivacyLink>,
    },
  );

  const onSubmit = useCallback<(data: ISignupForm & { terms: boolean }) => void>(
    values => {
      setIsSubmitting(true);
      (async () => {
        try {
          const token = (shouldUseRecaptcha && (await recaptchaRef.current?.executeAsync())) || '';
          const locale = getClientLocale();
          await onSignup({
            name: values.name,
            email: values.email,
            phone: values.phone,
            password: values.password,
            country: locale?.country,
            token,
            enableSms: values.enableSms,
          });

          if (router.pathname === '/become-a-host') {
            navigateTo(`${process.env.hostApp}/list-your-rv`);
          }
        } catch (exception) {
          logger.error(exception);
        } finally {
          reset();
        }
      })();
    },
    [onSignup, shouldUseRecaptcha, router.pathname],
  );

  const nameErr = errors.name && fieldErrors.name.generic;
  const emailErr = errors.email && fieldErrors.email[errors.email.type];
  const phoneErr =
    errors.phone && (fieldErrors.phone[errors.phone.type] || fieldErrors.phone.pattern);
  const passErr = errors.password && fieldErrors.password[errors.password.type];
  const termsErr = errors.terms && fieldErrors.terms[errors.terms.type];

  return (
    <form
      className="text-sm space-y-6"
      aria-label={messageInputSubmit}
      onSubmit={handleSubmit(onSubmit)}
      noValidate>
      {!!error && (
        <HelpText
          className="mb-2"
          text={typeof error === 'string' ? error : error.error || ''}
          variant={EHelpTextVariant.Error}
          iconBeforeText
        />
      )}
      <fieldset className="flex flex-col gap-y-6">
        <div className="grid grid-cols-1 gap-y-5 gap-x-4 md:grid-cols-2">
          <Controller
            name="name"
            control={control}
            rules={nameValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="name"
                autoComplete="name"
                label={messageInputName}
                ariaLabel={messageInputName}
                inputValue={field.value || ''}
                onChange={field.onChange}
                markAsRequired
                error={!!nameErr}
                helpText={nameErr}
                aria-required="true"
                aria-autocomplete="inline"
                aria-invalid={!!errors.name}
                aria-errormessage={nameErr}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={emailValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="email"
                autoComplete="email"
                label={messageInputEmail}
                ariaLabel={messageInputEmail}
                inputValue={field.value || ''}
                onChange={field.onChange}
                markAsRequired
                error={!!emailErr}
                helpText={emailErr}
                aria-required="true"
                aria-autocomplete="inline"
                aria-invalid={!!errors.email}
                aria-errormessage={emailErr}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={phoneValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="phone"
                autoComplete="tel"
                label={messageInputPhone}
                ariaLabel={messageInputPhone}
                inputValue={field.value || ''}
                onChange={field.onChange}
                markAsRequired
                error={!!phoneErr}
                helpText={phoneErr}
                aria-required="true"
                aria-autocomplete="inline"
                aria-invalid={!!errors.phone}
                aria-errormessage={phoneErr}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={setPasswordValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="password"
                autoComplete="new-password"
                label={messageInputPassword}
                ariaLabel={messageInputPassword}
                inputValue={field.value || ''}
                onChange={field.onChange}
                markAsRequired
                error={!!passErr}
                helpText={passErr}
                aria-required="true"
                aria-autocomplete="list"
                aria-invalid={!!errors.password}
                aria-errormessage={passErr}
                type={ETextInputTypes.Password}
                showPasswordButtonAriaLabel={showPasswordLabel}
              />
            )}
          />
        </div>
        <Controller
          name="terms"
          control={control}
          rules={termsValidator}
          render={({ field }) => (
            <Checkbox
              value="terms"
              isSelected={!!field.value}
              ariaLabel={intl.formatMessage({
                defaultMessage:
                  'I certify that I’m at least 18 years old at the time of sign up and agree to the Outdoorsy Terms of Service.',
                id: 'xMa0Hu',
              })}
              error={!!termsErr}
              disabled={isSubmitting}
              markAsRequired
              content={{
                label: <span>{messageConfirm}</span>,
              }}
              onChange={field.onChange}
              data-testid="terms-confirmation"
            />
          )}
        />
        <Controller
          name="enableSms"
          control={control}
          render={({ field }) => (
            <Checkbox
              isSelected={!!field.value}
              value="enableSms"
              ariaLabel={intl.formatMessage({
                defaultMessage:
                  'I agree to receive SMS messages from Outdoorsy to the phone number provided. Msg frequency varies. Msg&data rates may apply. Reply STOP to opt out, HELP for help. View Privacy Policy here.',
                id: '7FrznP',
              })}
              disabled={isSubmitting}
              content={{
                label: <span>{messageConfirmPhoneNotifications}</span>,
              }}
              onChange={field.onChange}
              data-testid="terms-enable-sms-confirmation"
            />
          )}
        />
      </fieldset>
      <Button
        type="submit"
        data-testid="signup-submit-btn"
        aria-disabled="false"
        variant={EButtonColorVariant.Primary}
        label={messageInputSubmit}
        disabled={isSubmitting || !didCheckTerms}
        loading={isSubmitting}
        fullWidth
      />
      {shouldUseRecaptcha && (
        <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={reCaptchaToken} />
      )}
    </form>
  );
};

export { SignUpForm };
